/**
 * Google Tag Manager Service
 * Handles GTM initialization, data layer management, and cookie consent integration
 * Complies with GDPR, CCPA, and PIPEDA requirements
 * GTM and data layer only run on production domain (digipalz.io, www.digipalz.io)
 */

import { COOKIE_CATEGORIES } from '../../constants/cookieCategories.js'
import consentService from '../cookiesPolicy/consentService.js'
import { isGTMAllowedByDomain } from '../../config/gtm.js'
import { isYouthEnvironment } from '../../config/youthEnvironment.js'

class GTMService {
  constructor() {
    this.gtmId = null
    this.isInitialized = false
    this.dataLayerName = 'dataLayer'
    this.consentUpdateQueue = []
    this.pageViewQueue = [] // Queue for page views before GTM is initialized
    this.consentMode = {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted' // Always granted for essential cookies
    }
  }

  /**
   * Check if GTM script is already loaded
   * @returns {boolean} True if GTM script is already in the DOM
   */
  isGTMScriptLoaded() {
    const scripts = document.querySelectorAll(
      'script[src*="googletagmanager.com/gtm.js"]'
    )
    return scripts.length > 0
  }

  /**
   * Initialize GTM with configuration
   * @param {object} config - GTM configuration
   * @param {string} config.gtmId - GTM container ID (GTM-XXXXXXX)
   * @param {string} config.dataLayerName - Custom data layer name (optional)
   * @param {object} config.defaultConsent - Default consent configuration
   */
  initialize(config = {}) {
    if (!isGTMAllowedByDomain()) return
    if (isYouthEnvironment()) return
    if (this.isInitialized) return

    // Get GTM ID from config or try to detect from existing script
    if (!config.gtmId) {
      // Try to extract GTM ID from existing script if GTM is pre-loaded
      const existingScript = document.querySelector(
        'script[src*="googletagmanager.com/gtm.js"]'
      )
      if (existingScript) {
        const match = existingScript.src.match(/id=([^&]+)/)
        if (match) {
          this.gtmId = match[1]
        }
      }

      if (!this.gtmId) return
    } else {
      this.gtmId = config.gtmId
    }

    this.dataLayerName = config.dataLayerName || 'dataLayer'

    try {
      // Initialize data layer (will use existing if already created)
      this.initializeDataLayer()

      // Check if GTM is already loaded from HTML head
      const gtmAlreadyLoaded = this.isGTMScriptLoaded()

      if (!gtmAlreadyLoaded) {
        this.loadGTMScript()
      }

      // Set initial consent mode and push to dataLayer (queued if GTM not ready)
      this.updateConsentMode()
      this.processConsentUpdateQueue()

      // Listen for consent changes
      this.setupConsentListener()

      this.isInitialized = true

      // Process any queued page views (only if consent is granted)
      if (this.pageViewQueue && this.pageViewQueue.length > 0) {
        const hasAnalyticsConsent = consentService.isConsentedForCategory(
          COOKIE_CATEGORIES.PERFORMANCE
        )

        if (hasAnalyticsConsent) {
          this.pageViewQueue.forEach((pageViewData) => {
            this.pushEvent('page_view', pageViewData)
          })
        }
        this.pageViewQueue = []
      }

      const pushGtmInitialized = () => {
        this.pushEvent('gtm_initialized', {
          gtm_id: this.gtmId,
          consent_mode: this.consentMode,
          pre_loaded: gtmAlreadyLoaded,
          timestamp: new Date().toISOString()
        })
      }
      setTimeout(pushGtmInitialized, 0)
    } catch (_) {}
  }

  /**
   * Initialize or get data layer
   */
  initializeDataLayer() {
    if (!window[this.dataLayerName]) {
      window[this.dataLayerName] = []
    }
    return window[this.dataLayerName]
  }

  /**
   * Load GTM script dynamically
   * Only called if GTM is not already loaded in the HTML head
   */
  loadGTMScript() {
    // Check if script is already loaded
    if (this.isGTMScriptLoaded()) return

    const dataLayer = this.initializeDataLayer()

    // Set up GTM configuration
    dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    })

    // Create and append GTM script
    // Ensure no debug/preview parameters are added in production
    const script = document.createElement('script')
    script.async = true

    // Build GTM URL - ensure no debug parameters in production
    let gtmUrl = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`

    // Remove any debug/preview parameters from URL if present
    const urlParams = new URLSearchParams(window.location.search)
    const hasDebugParam =
      urlParams.has('gtm_debug') || urlParams.has('gtm_preview')

    // Only allow debug/preview in development mode
    const isProduction =
      import.meta.env.PROD || import.meta.env.MODE === 'production'
    if (hasDebugParam && isProduction) {
      // Remove debug parameters from URL for production
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('gtm_debug')
      newUrl.searchParams.delete('gtm_preview')
      window.history.replaceState({}, '', newUrl.toString())
    }

    script.src = gtmUrl

    script.onload = () => {
      this.processConsentUpdateQueue()
    }

    script.onerror = () => {}

    document.head.appendChild(script)

    // Add noscript fallback (only if not already present)
    if (
      !document.querySelector('noscript iframe[src*="googletagmanager.com"]')
    ) {
      this.addNoScriptFallback()
    }
  }

  /**
   * Add noscript fallback for GTM
   */
  addNoScriptFallback() {
    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.gtmId}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'

    noscript.appendChild(iframe)
    document.body.insertBefore(noscript, document.body.firstChild)
  }

  /**
   * Update consent mode based on current consent preferences
   */
  updateConsentMode() {
    const preferences = consentService.getConsentPreferences()

    if (!preferences) {
      // No consent given - deny all except security
      this.consentMode = {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted'
      }
    } else {
      // Map cookie categories to GTM consent types
      this.consentMode = {
        // Marketing cookies -> ad consent
        ad_storage: preferences[COOKIE_CATEGORIES.MARKETING]
          ? 'granted'
          : 'denied',
        ad_user_data: preferences[COOKIE_CATEGORIES.MARKETING]
          ? 'granted'
          : 'denied',
        ad_personalization: preferences[COOKIE_CATEGORIES.MARKETING]
          ? 'granted'
          : 'denied',

        // Performance cookies -> analytics consent
        analytics_storage: preferences[COOKIE_CATEGORIES.PERFORMANCE]
          ? 'granted'
          : 'denied',

        // Functional cookies -> functionality consent
        functionality_storage: preferences[COOKIE_CATEGORIES.FUNCTIONAL]
          ? 'granted'
          : 'denied',
        personalization_storage: preferences[COOKIE_CATEGORIES.FUNCTIONAL]
          ? 'granted'
          : 'denied',

        // Essential cookies -> always granted
        security_storage: 'granted'
      }
    }

    // Push consent update to GTM
    this.pushConsent(this.consentMode)
  }

  /**
   * Push consent configuration to GTM
   * @param {object} consentConfig - Consent configuration object
   */
  pushConsent(consentConfig) {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', consentConfig)
    }

    const consentData = {
      consent: 'update',
      ...consentConfig
    }

    if (this.isInitialized) {
      this.pushToDataLayer(consentData)
    } else {
      // Queue for later if GTM not ready
      this.consentUpdateQueue.push(consentData)
    }
  }

  /**
   * Process queued consent updates
   */
  processConsentUpdateQueue() {
    while (this.consentUpdateQueue.length > 0) {
      const consentData = this.consentUpdateQueue.shift()
      this.pushToDataLayer(consentData)
    }
  }

  /**
   * Push event to data layer
   * @param {string} eventName - Event name
   * @param {object} eventData - Event data
   */
  pushEvent(eventName, eventData = {}) {
    const eventObject = {
      event: eventName,
      timestamp: new Date().toISOString(),
      ...eventData
    }

    this.pushToDataLayer(eventObject)
  }

  /**
   * Push data to GTM data layer
   * @param {object} data - Data to push
   */
  pushToDataLayer(data) {
    if (!isGTMAllowedByDomain()) return
    if (isYouthEnvironment()) return
    const dataLayer = this.initializeDataLayer()
    dataLayer.push(data)
  }

  /**
   * Setup listener for consent changes
   */
  setupConsentListener() {
    consentService.addConsentListener((preferences, action) => {
      // Update consent mode
      this.updateConsentMode()

      // Push consent change event
      this.pushEvent('consent_updated', {
        action,
        preferences,
        consent_mode: this.consentMode,
        categories_enabled: Object.keys(preferences).filter(
          (key) => preferences[key]
        ),
        categories_disabled: Object.keys(preferences).filter(
          (key) => !preferences[key]
        )
      })
    })
  }

  /**
   * Track custom event
   * @param {string} action - Event action
   * @param {object} properties - Event properties
   */
  trackEvent(action, properties = {}) {
    if (!this.isInitialized) return

    this.pushEvent('custom_event', {
      event_action: action,
      ...properties
    })
  }

  /**
   * Track page view
   * @param {object} pageData - Page data
   */
  trackPageView(pageData = {}) {
    if (isYouthEnvironment()) return

    // Check if analytics consent is granted before tracking
    const hasAnalyticsConsent = consentService.isConsentedForCategory(
      COOKIE_CATEGORIES.PERFORMANCE
    )

    if (!hasAnalyticsConsent) {
      // Don't track if consent not granted - GA will skip it anyway
      return
    }

    const pageViewData = {
      page_title: pageData.page_title || document.title,
      page_location: pageData.page_location || window.location.href,
      page_path: pageData.page_path || window.location.pathname,
      ...pageData
    }

    if (!this.isInitialized) {
      if (
        isGTMAllowedByDomain() &&
        this.isGTMScriptLoaded() &&
        window.dataLayer
      ) {
        window.dataLayer.push({
          event: 'page_view',
          ...pageViewData
        })
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'page_view', pageViewData)
        }
        return
      }

      // Queue for later if GTM not ready
      // Store in a queue that will be processed on initialization
      if (!this.pageViewQueue) {
        this.pageViewQueue = []
      }
      this.pageViewQueue.push(pageViewData)
      return
    }

    this.pushEvent('page_view', pageViewData)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', pageViewData)
    }
  }

  /**
   * Track user login
   * @param {object} userData - User data
   */
  trackLogin(userData = {}) {
    if (!this.isInitialized) return

    this.pushEvent('login', {
      method: 'email', // or social, etc.
      ...userData
    })
  }

  /**
   * Track user registration
   * @param {object} userData - User data
   */
  trackRegistration(userData = {}) {
    if (!this.isInitialized) return

    this.pushEvent('sign_up', {
      method: 'email',
      ...userData
    })
  }

  /**
   * Track purchase/subscription
   * @param {object} purchaseData - Purchase data
   */
  trackPurchase(purchaseData = {}) {
    if (!this.isInitialized) return

    this.pushEvent('purchase', {
      currency: 'USD',
      ...purchaseData
    })
  }

  /**
   * Track game interactions
   * @param {object} gameData - Game interaction data
   */
  trackGameInteraction(gameData = {}) {
    if (!this.isInitialized) return

    this.pushEvent('game_interaction', {
      event_category: 'Games',
      ...gameData
    })
  }

  /**
   * Track educational content engagement
   * @param {object} contentData - Content data
   */
  trackContentEngagement(contentData = {}) {
    if (!this.isInitialized) return

    this.pushEvent('content_engagement', {
      event_category: 'Education',
      ...contentData
    })
  }

  /**
   * Get current consent status
   * @returns {object} Current consent mode
   */
  getConsentStatus() {
    return {
      consent_mode: this.consentMode,
      preferences: consentService.getConsentPreferences(),
      last_updated: consentService.getConsentSummary().consentDate
    }
  }

  /**
   * Reset GTM (for testing purposes)
   */
  reset() {
    this.isInitialized = false
    this.gtmId = null
    this.consentUpdateQueue = []

    // Clear data layer
    if (window[this.dataLayerName]) {
      window[this.dataLayerName].length = 0
    }
  }

  /**
   * Get debug information
   * @returns {object} Debug information
   */
  getDebugInfo() {
    return {
      isInitialized: this.isInitialized,
      gtmId: this.gtmId,
      dataLayerName: this.dataLayerName,
      consentMode: this.consentMode,
      queueLength: this.consentUpdateQueue.length,
      dataLayerLength: window[this.dataLayerName]?.length || 0
    }
  }
}

// Create and export singleton instance
const gtmService = new GTMService()
export default gtmService
